import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cifra, useLang } from './i18n.jsx';
import { useAuth } from './auth.jsx';
import AccountPanel from './components/AccountPanel.jsx';
import Logo from './components/Logo.jsx';
import Nav from './components/Nav.jsx';
import Dropdown from './components/Dropdown.jsx';
import LangSwitch from './components/LangSwitch.jsx';
import CardDetail from './components/CardDetail.jsx';
import RefreshStatus from './components/RefreshStatus.jsx';
import Campana from './components/Campana.jsx';
import { cardArt } from './cardArt.js';
import { api } from './api.js';
import { RUTA_DE_VISTA, cartaDeRuta, vistaDeRuta, rutaDeEstado } from './rutas.js';
import Arte from './components/Arte.jsx';
import { Cargando, Fallo, Vacio } from './components/Decorado.jsx';
import Paginador from './components/Paginador.jsx';
import Subtabs from './components/Subtabs.jsx';
import MetaView from './components/MetaView.jsx';

/*
 * Las vistas grandes se cargan al entrar en ellas, no con la pagina.
 *
 * Los mazos, las partidas y la administracion son trozos aparte que Vite
 * separa solo con esto. En movil -- red lenta, datos caros -- es la diferencia
 * entre pintar la portada ya o hacer esperar por codigo que quiza no se use en
 * esta visita. El fallback es la pokebola de carga, la misma que en cualquier
 * otra espera.
 *
 * EL META NO: va con la pagina, porque ES la portada.
 *
 * Este bloque decia "la enciclopedia es la portada y carga siempre" y dejo de
 * ser verdad en jul-2026, cuando el meta se mudo a `/`. La portada se quedo
 * partida en un trozo aparte, asi que quien entra por la puerta principal
 * paga un viaje EN SERIE antes de ver nada: bajar el JS de la aplicacion,
 * interpretarlo, y solo entonces pedir el trozo del meta. Medido en
 * produccion, el elemento que marca el LCP es un parrafo de MetaView -- texto,
 * no una imagen --, o sea que ese viaje esta justo en el camino critico de la
 * metrica. Trocear la portada es partir lo unico que nadie puede evitar
 * descargar.
 */
const AdminView = lazy(() => import('./components/AdminView.jsx'));
const MazosView = lazy(() => import('./components/MazosView.jsx'));
const MatchesView = lazy(() => import('./components/MatchesView.jsx'));
const Funciones = lazy(() => import('./components/Funciones.jsx'));
const Sugerencias = lazy(() => import('./components/Sugerencias.jsx'));
const Privacidad = lazy(() => import('./components/Privacidad.jsx'));
const Buscador = lazy(() => import('./components/Buscador.jsx'));
const Metodologia = lazy(() => import('./components/Metodologia.jsx'));

const CARD_ROWS_PER_PAGE = 8;

/*
 * La API pagina por cantidad, pero la interfaz pagina por REJILLA. Un limite
 * fijo de 60 dejaba una ultima fila a medias en escritorio (suelen caber siete
 * cartas), aunque casualmente cuadrara en otros anchos. Esta cuenta replica
 * las dos reglas CSS de `.grid`: ancho util de `.app`, minimo de tarjeta y
 * hueco. El resultado siempre son ocho filas completas.
 */
function cardPageSize() {
  if (typeof window === 'undefined') return 56;
  const movil = window.innerWidth <= 720;
  const appWidth = movil
    ? window.innerWidth - 32
    : Math.min(window.innerWidth, 1320) - 56;
  const minCard = movil ? 112 : 150;
  const gap = movil ? 12 : 16;
  const columns = Math.max(1, Math.floor((appWidth + gap) / (minCard + gap)));
  return columns * CARD_ROWS_PER_PAGE;
}
const EMPTY_FILTERS = {
  type: null, category: null, rarity: null, stage: null, set: null, legal: 'standard',
  // Filtros avanzados (estilo Limitless): la API los soportaba desde el
  // principio (trainerType, mark, hpMin/hpMax) sin que la interfaz los
  // ofreciera. `hp` es un tramo "min-max" que queryString traduce a los dos
  // parametros reales.
  trainerType: null, mark: null, hp: null,
  // `retreat` es un tramo "min-max" como hp; `ability` es '1' o nada;
  // `artist` es texto libre (subcadena del nombre del ilustrador).
  retreat: null, weakness: null, ability: null, artist: null,
};

/* Los AVANZADOS: los que viven plegados tras «Más filtros». Trece
   desplegables siempre a la vista eran una muralla — la queja literal del
   refactor: "cuando quiero encontrar algo tengo que leer todo". Los seis de
   siempre bastan casi siempre; estos siete aparecen a demanda, y los chips
   de filtros activos siguen contando lo que haya puesto aunque el panel
   este plegado. */
const FILTROS_AVANZADOS = ['trainerType', 'mark', 'hp', 'retreat', 'weakness', 'ability', 'artist'];

const VISTAS = ['cards', 'meta', 'decks', 'matches', 'admin'];
const FILTROS_URL = [
  'type', 'category', 'rarity', 'stage', 'set', 'trainerType', 'mark', 'hp',
  'retreat', 'weakness', 'ability', 'artist',
];

/**
 * Logo de expansion con RESPALDO EN CADENA. El servidor manda candidatos en
 * orden (TCGdex del idioma, TCGdex ingles, pokemontcg.io) porque no puede
 * sondear el CDN sin bloquear la respuesta: aqui cada 404 pasa al siguiente
 * y, agotados todos, queda la tarjeta de texto -- nunca un icono roto.
 */
function SetLogo({ logos, textoSinLogo }) {
  const [i, setI] = useState(0);
  useEffect(() => setI(0), [logos]);
  if (!logos?.length || i >= logos.length) {
    return <span className="setcard__sinlogo">{textoSinLogo}</span>;
  }
  return <img src={logos[i]} alt="" loading="lazy" onError={() => setI((v) => v + 1)} />;
}

/**
 * Estado inicial leido de la URL.
 *
 * Busqueda, filtros, pagina y seccion viven tambien en la barra de direcciones:
 * sin esto, recargar (o compartir el enlace) tiraba lo que estabas mirando y
 * volvia a la portada.
 *
 * La SECCION viaja en el path (/meta, /mazos, /carta/ID), que es lo que un
 * buscador puede indexar como pagina propia. El `?vista=` antiguo se sigue
 * leyendo -- correos de pagos y enlaces compartidos viejos aterrizan bien -- y
 * el reflejo de abajo lo reescribe a la forma nueva en cuanto monta.
 *
 * `legal` tiene valor por defecto ('standard'), asi que "cualquiera" no se
 * puede escribir como ausencia: se escribe `legal=all`.
 */
function estadoDeUrl() {
  const p = new URLSearchParams(window.location.search);
  const filters = { ...EMPTY_FILTERS };
  for (const k of FILTROS_URL) if (p.get(k)) filters[k] = p.get(k);
  const legal = p.get('legal');
  if (legal) filters.legal = legal === 'all' ? null : legal;
  const path = window.location.pathname;
  const vistaVieja = p.get('vista');
  return {
    query: p.get('q') ?? '',
    filters,
    pagina: Math.max(1, Number.parseInt(p.get('p') ?? '1', 10) || 1),
    view: path === '/' && VISTAS.includes(vistaVieja) ? vistaVieja : vistaDeRuta(path),
    carta: cartaDeRuta(path),
    // Subpestaña de la enciclopedia: rejilla de cartas o navegador de
    // expansiones. Viaja en la URL como todo lo demas (?sub=sets).
    sub: p.get('sub') === 'sets' ? 'sets' : 'cards',
  };
}

/**
 * Avisos que llegan como parametro de la URL.
 *
 * El enlace del correo no puede abrir un panel por si mismo: aterriza en la
 * pagina y deja una marca. Aqui se lee, se actua y se LIMPIA la barra de
 * direcciones -- si el token de recuperacion se quedara en la URL, acabaria en
 * el historial del navegador y en cualquier captura de pantalla.
 */
/**
 * Detecta que se desplego una version nueva mientras esta pestaña seguia
 * abierta.
 *
 * EL PROBLEMA QUE RESUELVE. Los assets llevan huella y el index no se cachea,
 * asi que un RELOAD siempre trae lo ultimo -- pero nadie recarga una pestaña
 * que ya funciona. Quien la dejo abierta sigue ejecutando el JS viejo dias
 * enteros, y sus fallos parecen bugs nuestros ("los filtros no van", "no me
 * reconoce el plan"): imposibles de reproducir porque el codigo ya no existe.
 *
 * COMO. El propio script del documento lleva la huella en su nombre
 * (/assets/index-XXXX.js). Se pide el index fresco -- sin cache -- y se compara
 * la huella que anuncia con la que corre: si difieren, hay version nueva. Se
 * mira al VOLVER a la pestaña y cada 15 minutos; no en cada accion, porque el
 * despliegue es un suceso raro y esto no debe costar nada.
 *
 * En desarrollo el script es /src/main.jsx sin huella y el detector simplemente
 * no se activa.
 */
function useNuevaVersion() {
  const [hay, setHay] = useState(false);

  /*
   * ATAJO: si una vista diferida no se puede traer, ya sabemos que hay version
   * nueva -- sin esperar al sondeo de abajo.
   *
   * Vite avisa con `vite:preloadError` cuando falla el precargado de un trozo,
   * y eso solo pasa por un motivo en produccion: los ficheros con huella vieja
   * ya no existen porque se acaba de desplegar. `preventDefault()` evita que el
   * error suba y tumbe la vista: quien ya estaba dentro se queda donde estaba y
   * ve el aviso con su boton, en vez de perder la pantalla. El Salvavidas sigue
   * cubriendo el caso duro -- fallar durante la carga inicial --, que ahi no hay
   * nada que conservar.
   */
  useEffect(() => {
    const alFallarTrozo = (e) => {
      e.preventDefault();
      setHay(true);
    };
    window.addEventListener('vite:preloadError', alFallarTrozo);
    return () => window.removeEventListener('vite:preloadError', alFallarTrozo);
  }, []);

  useEffect(() => {
    const actual = document
      .querySelector('script[type="module"][src*="/assets/"]')
      ?.getAttribute('src');
    if (!actual) return undefined;
    let vivo = true;
    const mirar = async () => {
      try {
        const html = await (await fetch('/', { cache: 'no-store' })).text();
        const anunciada = html.match(/\/assets\/index-[^"']+\.js/)?.[0];
        if (vivo && anunciada && !actual.includes(anunciada)) setHay(true);
      } catch {
        /* sin red: ya se reintentara */
      }
    };
    const alVolver = () => {
      if (document.visibilityState === 'visible') mirar();
    };
    document.addEventListener('visibilitychange', alVolver);
    const cada = setInterval(mirar, 15 * 60 * 1000);
    return () => {
      vivo = false;
      document.removeEventListener('visibilitychange', alVolver);
      clearInterval(cada);
    };
  }, []);
  return hay;
}

function useUrlIntent() {
  const [intent, setIntent] = useState({ verificado: null, resetToken: null });

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const verificado = p.get('verificado');
    const resetToken = p.get('recuperar');
    if (!verificado && !resetToken) return;
    setIntent({ verificado, resetToken });
    p.delete('verificado');
    p.delete('recuperar');
    const q = p.toString();
    window.history.replaceState({}, '', window.location.pathname + (q ? `?${q}` : ''));
  }, []);

  return [intent, setIntent];
}

export default function App() {
  const { lang, t } = useLang();
  const { user, cargando: authCargando } = useAuth();
  const [accountOpen, setAccountOpen] = useState(false);
  /*
   * En que formulario abre el panel de cuenta. Los CTAs que prometen "crea tu
   * cuenta" (ProGate, sugerencias) deben aterrizar en el REGISTRO: abrir en
   * "entrar" y obligar a encontrar el enlace de "crear cuenta" era un paso mas
   * justo donde cada paso cuesta clientes.
   */
  const [modoCuenta, setModoCuenta] = useState('entrar');
  const [intent, setIntent] = useUrlIntent();
  const hayVersionNueva = useNuevaVersion();

  // Un enlace de recuperacion abre directamente el formulario correspondiente.
  useEffect(() => {
    if (intent.resetToken) setAccountOpen(true);
  }, [intent.resetToken]);

  // Cualquier pieza puede pedir el panel de cuenta sin recibir la funcion por
  // props (lo usa el ProGate cuando no hay sesion).
  useEffect(() => {
    const abrir = (e) => {
      setModoCuenta(e.detail?.modo === 'registro' ? 'registro' : 'entrar');
      setAccountOpen(true);
    };
    window.addEventListener('ptcg:abrir-cuenta', abrir);
    return () => window.removeEventListener('ptcg:abrir-cuenta', abrir);
  }, []);

  // Se lee UNA vez, al montar: despues manda el estado y la URL solo refleja.
  const [inicial] = useState(estadoDeUrl);

  const [meta, setMeta] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | empty | error
  const [query, setQuery] = useState(inicial.query);
  // Arranca IGUAL que `query`: si arrancara vacio, el debounce del montaje
  // "cambiaria" la busqueda y el efecto de abajo devolveria la pagina a la 1,
  // pisando la que venia en la URL.
  const [debounced, setDebounced] = useState(inicial.query);
  const [filters, setFilters] = useState(inicial.filters);
  /*
   * El campo de ilustrador escribe AQUI y el filtro real se aplica con
   * retardo: sin el, cada tecla disparaba una consulta y un salto de rejilla.
   * El efecto inverso mantiene el campo al dia cuando el filtro cambia por
   * fuera (atras del navegador, chip quitado, limpiar).
   */
  const [artistLocal, setArtistLocal] = useState(inicial.filters.artist ?? '');
  useEffect(() => {
    const id = setTimeout(() => {
      setFilters((f) => (f.artist === (artistLocal || null) ? f : { ...f, artist: artistLocal || null }));
    }, 350);
    return () => clearTimeout(id);
  }, [artistLocal]);
  useEffect(() => {
    setArtistLocal((v) => ((filters.artist ?? '') === v ? v : (filters.artist ?? '')));
  }, [filters.artist]);
  const [cards, setCards] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(cardPageSize);
  useEffect(() => {
    let raf = 0;
    const medir = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setPageSize((n) => {
        const siguiente = cardPageSize();
        return n === siguiente ? n : siguiente;
      }));
    };
    window.addEventListener('resize', medir);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', medir);
    };
  }, []);
  /**
   * Pagina de la rejilla. Paginador y no "cargar mas": con 3.000 cartas el
   * boton fabricaba una pagina kilometrica y sin nocion de donde estabas;
   * con paginas se puede ir al final, volver, y el pie no se aleja. Cambiar
   * la busqueda o un filtro vuelve a la primera, como en el meta.
   */
  const [pagina, setPagina] = useState(inicial.pagina);
  const [busy, setBusy] = useState(false);
  const [openCard, setOpenCard] = useState(null);
  /*
   * Cartas VECINAS de la ficha abierta, cuando se abrio desde una lista con
   * orden propio (la hoja de mazo). La rejilla de la enciclopedia calcula sus
   * vecinos alli mismo porque tiene las cartas a mano; el resto de vistas solo
   * mandan un id, asi que quien conozca el orden lo manda junto al id y aqui
   * se convierte en las flechas de anterior/siguiente. Sin lista, sin flechas:
   * mejor eso que flechas que saltan a un sitio sin relacion.
   */
  const [vecinasCarta, setVecinasCarta] = useState(null);
  const abrirCarta = useCallback((id, vecinas) => {
    setVecinasCarta(Array.isArray(vecinas) && vecinas.length > 1 ? vecinas : null);
    setOpenCard(id);
  }, []);
  const [view, setView] = useState(inicial.view);
  /*
   * NAVEGADOR DE EXPANSIONES, como subpestaña de la enciclopedia: la puerta
   * "por set" que Limitless tiene como pagina propia. El catalogo se pide al
   * entrar y se guarda por sesion; elegir un set salta a la rejilla con su
   * filtro puesto y SIN el recorte de formato (un set viejo con legal=standard
   * pintaria una pagina casi vacia que parece un fallo).
   */
  const [subCartas, setSubCartas] = useState(inicial.sub);
  const [setsCat, setSetsCat] = useState(null);
  /*
   * Si el catalogo de expansiones no se pudo pedir.
   *
   * Antes el fallo se guardaba como lista vacia y la subpestaña pintaba una
   * rejilla de cero elementos: ni pokebola, ni aviso, ni un texto. Un hueco
   * en blanco no se lee como "algo fue mal", se lee como "esta pagina esta
   * sin terminar". El nonce es lo que deja reintentar sin recargar.
   */
  const [setsFallo, setSetsFallo] = useState(false);
  const [setsNonce, setSetsNonce] = useState(0);
  useEffect(() => setSetsCat(null), [lang]);   // el nombre del set es por idioma
  useEffect(() => {
    if (view !== 'cards' || subCartas !== 'sets' || setsCat) return undefined;
    let alive = true;
    api(`/api/sets?lang=${lang}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => {
        if (!alive) return;
        setSetsCat(d.sets ?? []);
        setSetsFallo(false);
      })
      .catch(() => alive && setSetsFallo(true));
    return () => {
      alive = false;
    };
  }, [view, subCartas, lang, setsCat, setsNonce]);
  const elegirSet = (s) => {
    setFilters({ ...EMPTY_FILTERS, legal: null, set: s.id });
    setQuery('');
    setSubCartas('cards');
  };

  /*
   * Un enlace directo a /carta/ID aterriza con la ficha abierta: es la URL que
   * reparte el sitemap y la que se comparte por WhatsApp. Se abre cuando la
   * SESION ya se conoce, no en el montaje: la llegada del usuario remonta la
   * subvista (la key lleva user.id) y eso desmonta el modal recien abierto,
   * cuyo useVolverCierra dispara su back() de limpieza y lo cierra solo. Para
   * el visitante anonimo la espera es la comprobacion de sesion: nada.
   */
  useEffect(() => {
    if (!authCargando && inicial.carta) setOpenCard(inicial.carta);
  }, [authCargando, inicial.carta]);

  /*
   * Mazo cuyas partidas se quieren ver, al saltar desde la vista de mazos.
   *
   * Se guarda aqui y no dentro de Partidas porque el salto cruza dos vistas: se
   * pulsa en una y el efecto ocurre en la otra. Se limpia al entrar a Partidas
   * por las buenas -- pulsando la pestaña --, que si no, volver a esa pestaña
   * mas tarde reaplicaria un filtro que nadie pidio esta vez.
   */
  const [partidasDeMazo, setPartidasDeMazo] = useState(null);
  /*
   * Destinos del buscador global que viven en OTRA vista. Mismo patron y mismo
   * motivo que `partidasDeMazo`: el salto cruza dos vistas y lleva contador
   * para que pedir dos veces lo mismo vuelva a surtir efecto.
   */
  const [arquetipoInicial, setArquetipoInicial] = useState(null);
  const [mazoInicial, setMazoInicial] = useState(null);
  const [verFunciones, setVerFunciones] = useState(false);
  const [verSugerencias, setVerSugerencias] = useState(false);
  const [verPrivacidad, setVerPrivacidad] = useState(false);
  /*
   * La metodologia se abre desde DOS sitios lejanos: el pie y la cabecera del
   * meta. Se escucha un evento en vez de bajar una prop cuatro niveles hasta
   * MetaView, que es el mismo camino que ya usa el panel de cuenta
   * (`ptcg:abrir-cuenta`) y por el mismo motivo.
   */
  const [verMetodologia, setVerMetodologia] = useState(false);
  /*
   * El buscador global. Se abre con Ctrl/Cmd+K -- el atajo que ya tiene todo
   * el mundo en los dedos -- y con la lupa de la barra, que es el unico camino
   * en un telefono.
   */
  const [verBuscador, setVerBuscador] = useState(false);
  /* En Mac el modificador es ⌘ y en el resto Ctrl: anunciar el que no es deja
     el atajo sin usar y de paso delata que no se miro. */
  const atajoBuscar = useMemo(
    () => (/Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent) ? '⌘K' : 'Ctrl K'),
    []
  );
  useEffect(() => {
    const alTeclado = (e) => {
      if (e.key !== 'k' && e.key !== 'K') return;
      if (!(e.metaKey || e.ctrlKey)) return;
      /* Se pisa el "buscar en la pagina" del navegador a proposito: dentro de
         una aplicacion, Cmd+K es abrir SU buscador, y el del navegador no
         sirve de nada sobre una lista que se pide al servidor. */
      e.preventDefault();
      setVerBuscador(true);
    };
    window.addEventListener('keydown', alTeclado);
    return () => window.removeEventListener('keydown', alTeclado);
  }, []);
  useEffect(() => {
    const abrir = () => setVerMetodologia(true);
    window.addEventListener('ptcg:abrir-metodologia', abrir);
    return () => window.removeEventListener('ptcg:abrir-metodologia', abrir);
  }, []);

  /* "Ver el meta" desde el vacio de Mazos: el tercer camino para tener un mazo
     no vive en Mazos sino en el meta, donde las listas de torneo se guardan de
     un boton. Por evento y no por prop porque cruza dos vistas. */
  useEffect(() => {
    const irAlMeta = () => setView('meta');
    window.addEventListener('ptcg:ir-al-meta', irAlMeta);
    return () => window.removeEventListener('ptcg:ir-al-meta', irAlMeta);
  }, []);

  // Cualquier pieza puede abrir la politica sin recibir la funcion por props
  // (la usa el formulario de registro, que vive tres niveles mas abajo).
  useEffect(() => {
    const abrir = () => setVerPrivacidad(true);
    window.addEventListener('ptcg:abrir-privacidad', abrir);
    return () => window.removeEventListener('ptcg:abrir-privacidad', abrir);
  }, []);
  /**
   * En telefono los seis filtros ocupaban 200 px de pantalla ANTES de la
   * primera carta, se usaran o no. Se pliegan tras un boton junto al buscador;
   * en escritorio sobra sitio y siguen siempre a la vista (el boton ni se
   * pinta: lo esconde el CSS, no una condicion de JS, para no duplicar la
   * logica de puntos de corte).
   */
  const [verFiltros, setVerFiltros] = useState(false);
  /* «Más filtros» nace CERRADO — salvo que la URL ya traiga un avanzado
     puesto: plegar un filtro activo escondería el control de lo que esta
     acotando los resultados. Solo se mira al montar, a proposito: es el
     estado inicial, no un seguimiento. */
  const [masFiltros, setMasFiltros] = useState(
    () => FILTROS_AVANZADOS.some((k) => filters[k] !== EMPTY_FILTERS[k])
  );

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query), 250);
    return () => clearTimeout(id);
  }, [query]);

  /*
   * Una ingesta del panel de "Datos" acaba de terminar: el catálogo pudo
   * cambiar y la rejilla debe volver a pedir su página. El nonce reengancha el
   * efecto de abajo sin tocar búsqueda, filtros ni página. El meta hace lo
   * propio dentro de MetaView, con el mismo aviso.
   */
  const [nonceDatos, setNonceDatos] = useState(0);
  useEffect(() => {
    const alFresco = () => setNonceDatos((n) => n + 1);
    window.addEventListener('ptcg:datos-frescos', alFresco);
    return () => window.removeEventListener('ptcg:datos-frescos', alFresco);
  }, []);

  // El vocabulario de filtros se recarga al cambiar de idioma porque las
  // ETIQUETAS cambian, aunque las claves que se envian a la API no.
  const loadMeta = useCallback(() => {
    setStatus('loading');
    api(`/api/meta?lang=${lang}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('meta'))))
      .then((m) => {
        setMeta(m);
        setStatus(m.sets?.length ? 'ready' : 'empty');
      })
      .catch(() => setStatus('error'));
  }, [lang]);

  useEffect(loadMeta, [loadMeta]);

  /*
   * CADENA y no URLSearchParams: los efectos que dependen de esto comparan por
   * identidad, y al restaurar desde el historial (popstate) los filtros llegan
   * como objeto nuevo con los mismos valores. Con la cadena, "igual" es igual
   * y restaurar no re-dispara la busqueda ni devuelve la pagina a la 1.
   */
  const queryString = useMemo(() => {
    const p = new URLSearchParams({ lang, limit: String(pageSize) });
    if (debounced.trim()) p.set('q', debounced.trim());
    for (const [k, v] of Object.entries(filters)) {
      if (!v) continue;
      // Los tramos viajan como los dos limites que la API entiende;
      // "240-" (sin techo) solo manda el suelo, "-0" solo el techo.
      if (k === 'hp' || k === 'retreat') {
        const [min, max] = v.split('-');
        const pre = k === 'hp' ? 'hp' : 'retreat';
        if (min !== '') p.set(`${pre}Min`, min);
        if (max !== '') p.set(`${pre}Max`, max);
      } else if (k === 'ability') {
        p.set('hasAbility', '1');
      } else {
        p.set(k, v);
      }
    }
    return p.toString();
  }, [lang, debounced, filters, pageSize]);

  // Cambiar busqueda o filtros vuelve a la primera pagina -- pero NO en el
  // montaje, que es cuando la pagina acaba de llegar de la URL.
  const primeraPasada = useRef(true);
  useEffect(() => {
    if (primeraPasada.current) {
      primeraPasada.current = false;
      return;
    }
    setPagina(1);
  }, [queryString]);

  /*
   * La URL refleja el estado, en cada cambio. Cambiar de PESTAÑA apila entrada
   * (pushState): es navegacion de verdad y "atras" debe deshacerla. Todo lo
   * demas -- teclear, filtrar, paginar -- reemplaza (replaceState): apilar por
   * tecla convertiria "atras" en deshacer la busqueda letra a letra. Las
   * claves ajenas (verificado, recuperar) se respetan, y el `state` existente
   * se conserva al reemplazar: los modales guardan ahi su profundidad (ver
   * useVolverCierra en movimiento.js).
   */
  const restaurando = useRef(false);
  const vistaEnUrl = useRef(inicial.view);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const pon = (k, v) => (v ? p.set(k, v) : p.delete(k));
    // La seccion va en el path; el `vista=` de un enlace viejo se limpia aqui.
    p.delete('vista');
    pon('q', debounced.trim() || null);
    for (const k of FILTROS_URL) pon(k, filters[k]);
    pon('legal', filters.legal === EMPTY_FILTERS.legal ? null : (filters.legal ?? 'all'));
    pon('p', pagina > 1 ? String(pagina) : null);
    pon('sub', view === 'cards' && subCartas === 'sets' ? 'sets' : null);
    const q = p.toString();
    /*
     * La carta abierta manda sobre la vista: /carta/ID es una URL compartible
     * y ademas la que el efecto REEMPLAZA sobre la entrada que useVolverCierra
     * acaba de apilar (los efectos del hijo corren antes que los del padre, asi
     * que la entrada del modal ya existe cuando esto escribe su URL). Al
     * cerrarse por interfaz, el back() aplazado del hook devuelve a la entrada
     * anterior con su URL de siempre.
     */
    const url = rutaDeEstado(view, openCard) + (q ? `?${q}` : '');

    const cambioVista = view !== vistaEnUrl.current;
    vistaEnUrl.current = view;
    /*
     * Aviso a la analitica (GTM): en esta SPA la "pagina" es la pestaña. El
     * evento sale tanto al pulsar como al volver con "atras" -- ambos son
     * navegacion de verdad --; sin el, la medicion solo veria la carga
     * inicial. La carga inicial no se avisa: esa ya la registra GTM solo.
     */
    /*
     * La marca se relee en cada aviso y no una vez al cargar: si la exclusión
     * se activa en caliente (el interruptor del panel, o entrar con la cuenta
     * excluida), GTM ya está cargado en esta sesión y sin esto seguiría
     * contando vistas hasta la próxima recarga.
     */
    let excluido = false;
    try {
      excluido =
        localStorage.getItem('pt_sin_analytics') === '1' ||
        localStorage.getItem('pt_sin_analytics_cuenta') === '1';
    } catch { /* sin localStorage, se cuenta como siempre */ }
    if (cambioVista && !excluido) {
      window.dataLayer?.push({ event: 'vista', vista: view, page_path: url });
    }
    if (cambioVista && !restaurando.current) {
      /*
       * Con una capa apilada por useVolverCierra (un mazo en detalle, una
       * ficha abierta), la pestaña nueva REEMPLAZA su entrada en vez de
       * apilar encima: apilar dejaria la entrada de la capa enterrada bajo
       * la nueva y "atras" aterrizaria en una capa que ya no existe. El
       * estado se limpia a {} adrede: la capa murio con el cambio de vista
       * y su marca `modal` no debe sobrevivirle en el historial.
       */
      if ((window.history.state?.modal ?? 0) > 0) {
        window.history.replaceState({}, '', url);
      } else {
        window.history.pushState({}, '', url);
      }
      /*
       * Pestaña nueva, pagina por arriba: al no haber navegacion de verdad el
       * scroll de la pestaña anterior se heredaba. Solo en el cambio DIRECTO:
       * al volver con "atras" (restaurando) no se toca, porque el navegador
       * restaura solo el scroll que cada entrada tenia, que es lo esperado.
       */
      window.scrollTo({ top: 0 });
    } else {
      window.history.replaceState(window.history.state ?? {}, '', url);
    }
    restaurando.current = false;
  }, [view, debounced, filters, pagina, openCard, subCartas]);

  /*
   * "Atras" y "adelante" del navegador: se restaura lo que diga la URL de la
   * entrada en la que se aterrizo. Las capas de modal se gestionan solas
   * (useVolverCierra); aqui solo el estado que viaja en la URL. `restaurando`
   * evita que el reflejo de arriba vuelva a apilar la vista que el historial
   * acaba de sacar.
   */
  useEffect(() => {
    const onPop = () => {
      const s = estadoDeUrl();
      restaurando.current = true;
      setView(s.view);
      setQuery(s.query);
      setDebounced(s.query);
      setFilters(s.filters);
      setPagina(s.pagina);
      setSubCartas(s.sub);
      /*
       * La carta abierta NO se toca aqui, ni para abrir ni para cerrar.
       * Cerrarla por "atras" es el trabajo de useVolverCierra (CardDetail), y
       * hacerlo tambien desde este listener lo rompia: React desmonta el
       * modal en el microtask que corre ENTRE los dos listeners de popstate,
       * la limpieza de useVolverCierra no ve su cierre-por-historial (viaPop)
       * y dispara su back() de respaldo: dos "atras" por uno. Y abrirla desde
       * aqui remontaria el modal, que apilaria otra entrada y convertiria
       * "adelante" en una trampa. El unico que abre por URL es el aterrizaje
       * inicial (efecto de arriba).
       */
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    if (status !== 'ready') return;
    let alive = true;
    setBusy(true);
    const p = new URLSearchParams(queryString);
    p.set('offset', String((pagina - 1) * pageSize));
    api(`/api/cards?${p}`)
      .then((r) => r.json())
      .then((d) => {
        if (!alive) return;
        setCards(d.cards ?? []);
        setTotal(d.total ?? 0);
      })
      .finally(() => alive && setBusy(false));
    return () => {
      alive = false;
    };
  }, [queryString, status, pagina, nonceDatos]);

  /** Pasar pagina desde el pie de la rejilla: arriba, donde empieza la nueva. */
  const irPagina = (n) => {
    setPagina(n);
    window.scrollTo({ top: 0 });
  };

  const anyOption = { key: null, label: t('any') };
  const opts = (list) => [anyOption, ...(list ?? [])];
  const setFilter = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));
  const filtersActive =
    Object.entries(filters).some(([k, v]) => v !== EMPTY_FILTERS[k]) || query.trim() !== '';

  /*
   * CHIPS de filtros activos, sobre los resultados. Nueve desplegables se
   * escanean mal ("¿que tengo puesto?" obligaba a leerlos uno a uno, y en
   * movil ni se ven sin abrir el panel): los chips son la respuesta de un
   * vistazo, y cada uno se quita con su aspa sin buscar su desplegable.
   */
  const NOMBRE_FILTRO = {
    legal: 'format', type: 'type', category: 'category', stage: 'stage',
    rarity: 'rarity', set: 'set', trainerType: 'filterTrainerType',
    mark: 'filterMark', hp: 'hp', retreat: 'retreat', weakness: 'weakness',
    ability: 'filterAbility', artist: 'filterArtist',
  };
  const HP_LABELS = { '-70': '≤ 70', '80-140': '80 – 140', '150-230': '150 – 230', '240-': '240 +' };
  const RETREAT_LABELS = { '0-0': '0', '1-1': '1', '2-2': '2', '3-': '3 +' };
  const etiquetaFiltro = (k, v) => {
    switch (k) {
      case 'legal':
        return v === 'standard' ? t('standard') : v === 'expanded' ? t('expanded') : t('any');
      case 'type': return meta?.types?.find((x) => x.key === v)?.label ?? v;
      case 'category': return meta?.categories?.find((x) => x.key === v)?.label ?? v;
      case 'stage': return meta?.stages?.find((x) => x.key === v)?.label ?? v;
      case 'rarity': return meta?.rarities?.find((x) => x.key === v)?.label ?? v;
      case 'trainerType': return meta?.trainerTypes?.find((x) => x.key === v)?.label ?? v;
      case 'set': return meta?.sets?.find((x) => x.id === v)?.name ?? v;
      case 'hp': return HP_LABELS[v] ?? v;
      case 'retreat': return RETREAT_LABELS[v] ?? v;
      case 'weakness': return meta?.types?.find((x) => x.key === v)?.label ?? v;
      case 'ability': return t('abilityYes');
      case 'artist': return `«${v}»`;
      default: return String(v);
    }
  };
  /*
   * QUE SECCION ESTA ABIERTA, en la raiz del DOCUMENTO y no en la de la app.
   *
   * La hoja traduce `data-modulo` a var(--modulo), y de ahi salen la luz del
   * fondo, el filo de los paneles, la pildora de la navegacion y el borde
   * superior de los modales.
   *
   * Va en <html> por los MODALES. Todos se pintan con `createPortal` sobre
   * `document.body`, o sea que NO son descendientes de `.app`: colgando el
   * atributo del div de la aplicacion, las variables CSS -- que solo heredan
   * hacia abajo -- no llegaban a la ficha de carta ni al panel de cuenta, y su
   * filo salia del dorado de reserva en las cinco secciones. Sin dar ningun
   * error: simplemente el color no era el que se habia pedido.
   *
   * Desde <html> lo hereda todo el documento, portales incluidos.
   */
  useEffect(() => {
    document.documentElement.dataset.modulo = view;
  }, [view]);

  const filtrosPuestos = Object.entries(filters).filter(([k, v]) => v !== EMPTY_FILTERS[k]);

  if (status === 'error' || status === 'empty') {
    const isEmpty = status === 'empty';
    return (
      <div className="app">
        <div className="notice">
          <Vacio
            tipo="pokebola"
            titulo={isEmpty ? t('dbEmpty') : t('apiDown')}
            texto={isEmpty ? t('dbEmptyHint') : t('apiDownHint')}
          >
            <button type="button" className="btn" onClick={loadMeta}>
              {t('retry')}
            </button>
          </Vacio>
        </div>
        <a
          className="credito-asery center"
          href="https://asery.cl?utm_source=poketrack"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('madeBy')} <strong>A-Sery lab</strong>
        </a>
      </div>
    );
  }

  return (
    /* `data-modulo` lo estampa el efecto de arriba en <html>, no aquí: los
       modales van por portal a `body` y desde este div no los alcanzaba. */
    <div className="app">
      {/*
        Saltar al contenido. Invisible hasta que el foco lo toca, y entonces
        baja de la nada arriba a la izquierda.

        Quien navega con teclado (o con lector) empieza cada pantalla por la
        marca, las herramientas, la campana, la cuenta y las cinco pestañas:
        una docena de paradas antes de llegar a lo que ha venido a leer, y otra
        vez enteras al cambiar de seccion. Es el primer elemento del documento
        a proposito -- si no es lo primero, no sirve para nada.
      */}
      <a className="saltar" href="#contenido">
        {t('skipToContent')}
      </a>
      {/*
        UNA SOLA CABECERA. La marca y las herramientas eran una barra propia
        encima de las pestañas: dos franjas para una navegacion, y la de
        arriba -- con el buscador, la campana y la cuenta -- se iba con el
        scroll. Nav las pinta ahora en la misma zona pegajosa, a los lados de
        la pildora; aqui solo se COMPONEN, porque su estado (buscador, sesion,
        cuenta) vive en App.
      */}
      <Nav
        items={[
          /*
             El META abre la lista porque es la portada: lo primero de la barra
             y lo primero que se ve al entrar cuentan la misma historia. Despues
             el recorrido real: consultas las CARTAS, montas tu MAZO y registras
             tus PARTIDAS con él. */
          { key: 'meta', label: t('navMeta') },
          { key: 'cards', label: t('navCards') },
          { key: 'decks', label: t('navDecks') },
          /* `short` es el rótulo del dock del teléfono: ahí "Mis partidas" se
             partía en dos líneas y crecía la barra entera (ver i18n). */
          { key: 'matches', label: t('navMatches'), short: t('navMatchesShort') },
          // La pestaña sólo se pinta para el admin, pero eso es comodidad: cada
          // ruta comprueba el rol en el servidor. Esconder un botón no protege.
          ...(user?.role === 'admin' ? [{ key: 'admin', label: t('navAdmin') }] : []),
        ]}
        view={view}
        onChange={(v) => {
          /* Entrar a Partidas por la pestaña es entrar en limpio: el salto
             desde otra vista (ver las partidas de un mazo) vale para ESE viaje
             y no debe reaplicarse la próxima vez que alguien pulse la pestaña. */
          if (v === 'matches') setPartidasDeMazo(null);
          /*
           * Al SALIR de Cartas, la busqueda y los filtros se limpian: volver a
           * la pestaña dias -- o minutos -- despues y encontrarse "yvelt" con
           * cuatro resultados parecia una pestaña rota, no una busqueda vieja.
           * Solo al navegar por pestañas; "atras" restaura lo suyo desde el
           * historial, que ahi si se espera volver a lo que se estaba viendo.
           */
          if (view === 'cards' && v !== 'cards') {
            setQuery('');
            setFilters(EMPTY_FILTERS);
          }
          setView(v);
        }}
        brand={
          <div className="topbar__brand">
            <Logo size={38} />
            {/*
              La marca NO es el h1.

              Estaba de <h1> en la barra, que se pinta en todas las pantallas:
              para un lector -- y para Google -- las cinco secciones del sitio
              se titulaban igual, "PokeTrack", y el titulo de la pagina no
              decia nunca de que iba la pagina. El h1 es de la VISTA (el meta,
              la enciclopedia, los mazos...) y lo pone cada una; aqui queda un
              parrafo con la misma pinta de siempre -- `.topbar__title` ya
              lleva la tipografia de display por su clase, no por la etiqueta.

              (El eslogan tampoco vive aqui: repetia la entradilla del meta en
              las cinco secciones; el pie conserva su "ver todo lo que hace".)
            */}
            <div className="topbar__brandcopy">
              <p className="topbar__title">
                Poke<span className="topbar__title--accent">Track</span>
              </p>
              <span className="topbar__tagline">{t('brandTagline')}</span>
            </div>
          </div>
        }
        tools={
          <div className="topbar__tools">
            {/* La lupa. En escritorio enseña su atajo -- un atajo que no se
                anuncia no lo usa nadie --; en el telefono es el unico camino al
                buscador, asi que ahi no puede faltar. */}
            <button
              type="button"
              className="buscabtn"
              onClick={() => setVerBuscador(true)}
              aria-label={t('buscadorTitulo')}
            >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.7" />
                <path d="m16 16 4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
              </svg>
              <span className="buscabtn__label">{t('buscadorTitulo')}</span>
              <span className="buscabtn__atajo" aria-hidden="true">{atajoBuscar}</span>
            </button>
            {/* Solo para el administrador: dispara ingestas y pide clave de
                escritura -- nada que le sirva a quien viene a consultar. */}
            {user?.role === 'admin' && <RefreshStatus />}
            {/* La campana solo existe con sesion: es de cosas TUYAS. */}
            <Campana />
            {/* Mientras se comprueba la sesion no se pinta nada: mostrar
                "Entrar" y cambiarlo al nombre un instante despues se lee como
                un fallo. Sin sesion, el CTA solido de la casa: la puerta a la
                cuenta es lo unico de la barra que gana dinero. */}
            {!authCargando && (
              <button
                type="button"
                className={`accbtn${user ? ' is-in' : ' accbtn--cta'}`}
                onClick={() => {
                  setModoCuenta('entrar');
                  setAccountOpen(true);
                }}
                title={user ? t('myAccount') : t('signIn')}
              >
                {user ? (
                  <>
                    <span className="accbtn__avatar" aria-hidden="true">
                      {(user.name || user.email)[0].toUpperCase()}
                    </span>
                    <span className="accbtn__name">{user.name || user.email.split('@')[0]}</span>
                    {!user.emailVerified && <span className="accbtn__dot" title={t('verifyPending')} />}
                  </>
                ) : (
                  t('signIn')
                )}
              </button>
            )}
          </div>
        }
      />

      {/*
        Aviso de version nueva. NO recarga sola: quien esta a mitad de etiquetar
        su historial no quiere que la pagina se le vaya debajo de las manos. Un
        boton y que decida.
      */}
      {hayVersionNueva && (
        <div className="banner is-ok" role="status">
          <span>{t('newVersionReady')}</span>
          <button type="button" className="btn" onClick={() => window.location.reload()}>
            {t('reloadNow')}
          </button>
        </div>
      )}

      {intent.verificado && (
        <div className={`banner${intent.verificado === 'si' ? ' is-ok' : ' is-warn'}`} role="status">
          <span>{intent.verificado === 'si' ? t('verifiedOk') : t('verifiedExpired')}</span>
          <button
            type="button"
            onClick={() => setIntent((i) => ({ ...i, verificado: null }))}
            aria-label={t('close')}
          >
            ×
          </button>
        </div>
      )}

      <AccountPanel
        open={accountOpen}
        onClose={() => {
          setAccountOpen(false);
          setIntent((i) => ({ ...i, resetToken: null }));
        }}
        modoInicial={intent.resetToken ? 'reset' : modoCuenta}
        resetToken={intent.resetToken}
      />

      {/*
        La `key` es lo que hace la transicion: al cambiar de pestaña React
        monta un nodo distinto y la animacion de entrada vuelve a correr. Sin
        ella el contenido se sustituye en seco.

        Se anima solo la ENTRADA, no la salida. Encadenar las dos obligaria a
        mantener la vista anterior viva unos milisegundos, y con MetaView eso
        significa dos vistas pidiendo datos a la vez; ademas retrasa la
        navegacion, que es justo lo que no se quiere al pulsar una pestaña.
      */}
      {/*
        La identidad va en la key ADEMAS de la pestaña: al entrar o salir de la
        cuenta, la vista abierta se remonta y repide sus datos con la sesion
        nueva. Sin esto, quien se logueaba sobre el meta seguia viendo la
        version de visitante (3 arquetipos, datos con retraso) hasta recargar.
        Van id y plan -- valores estables --, no el objeto `user`: el refresco
        periodico crea un objeto nuevo cada 12 minutos y remontaria en vano.
      */}
      {/*
        `main` y no `div`: es el punto de aterrizaje del enlace de salto y la
        region que un lector de pantalla ofrece como "el contenido" frente a
        la cabecera, la navegacion y el pie. La clase no cambia, asi que el CSS
        (la animacion de entrada y los `.vista .meta` de abajo) sigue igual.

        `tabIndex={-1}` porque un `main` no es enfocable de suyo: sin el, en
        varios navegadores el salto mueve el scroll pero deja el foco donde
        estaba y el siguiente Tab vuelve a la cabecera -- el salto no habria
        servido de nada justo para quien lo usa.
      */}
      <main
        className="vista"
        id="contenido"
        tabIndex={-1}
        key={`${view}:${user?.id ?? 0}:${user?.plan ?? 'free'}`}
      >
      {view !== 'cards' ? (
        <Suspense fallback={<Cargando texto={t('loading')} />}>
          {view === 'decks' && (
            <MazosView
              onOpenCard={abrirCarta}
              mazoInicial={mazoInicial}
              onVerPartidas={(id) => {
                setPartidasDeMazo(id);
                setView('matches');
              }}
            />
          )}
          {view === 'meta' && (
            <MetaView
              onOpenCard={abrirCarta}
              arquetipoInicial={arquetipoInicial}
            />
          )}
          {view === 'matches' && (
            /* onOpenCard: la hoja del mazo rival abre fichas de carta, y el
               CardDetail lo pinta esta misma rama del Suspense. */
            <MatchesView
              deckInicial={partidasDeMazo}
              onOpenCard={abrirCarta}
            />
          )}
          {view === 'admin' && user?.role === 'admin' && <AdminView />}
          {openCard && (() => {
            /*
             * Mismas flechas que en la enciclopedia, pero el orden lo puso
             * quien abrio la ficha (la hoja de mazo manda sus 60 cartas en
             * orden visual). Anterior/siguiente reusan setOpenCard a secas
             * para conservar la lista mientras se navega dentro de ella.
             */
            const idx = vecinasCarta ? vecinasCarta.indexOf(openCard) : -1;
            const prev = idx > 0 ? vecinasCarta[idx - 1] : null;
            const next = idx >= 0 && idx < vecinasCarta.length - 1 ? vecinasCarta[idx + 1] : null;
            return (
              <CardDetail
                cardId={openCard}
                onClose={() => setOpenCard(null)}
                onPrev={prev ? () => setOpenCard(prev) : undefined}
                onNext={next ? () => setOpenCard(next) : undefined}
              />
            );
          })()}
        </Suspense>
      ) : (
      <>
      {/*
        El titulo de la enciclopedia existe pero no se ve: arriba manda el
        buscador y meterle un rotulo encima empujaria hacia abajo lo unico que
        se viene a usar. Para un lector de pantalla, en cambio, es lo que
        contesta "¿donde estoy?" al aterrizar, y sin el la pantalla empezaba
        directamente en un campo de busqueda sin nombre de pagina.
      */}
      <h1 className="sr-only">{t('navCards')}</h1>
      {/* Dos puertas a la enciclopedia: por carta (buscador + filtros) o por
          expansion (el navegador de sets). Mismo patron de subpestañas que el
          meta, para que el gesto ya aprendido sirva aqui. */}
      <Subtabs
        label={t('navCards')}
        value={subCartas}
        onChange={setSubCartas}
        items={[
          { key: 'cards', label: t('subAllCards') },
          { key: 'sets', label: t('subSets') },
        ]}
      />

      {subCartas === 'sets' ? (
        setsFallo && !setsCat ? (
          <Fallo
            titulo={t('loadFailTitle')}
            texto={t('loadFailHint')}
            textoReintentar={t('retry')}
            onReintentar={() => {
              setSetsFallo(false);
              setSetsNonce((n) => n + 1);
            }}
          />
        ) : !setsCat ? (
          <Cargando texto={t('loading')} />
        ) : (
          <ul className="setgrid">
            {setsCat.map((s) => (
              <li key={s.id}>
                <button type="button" className="setcard" onClick={() => elegirSet(s)}>
                  <span className="setcard__logo">
                    <SetLogo logos={s.logos} textoSinLogo={s.ptcglCode ?? s.name} />
                  </span>
                  <span className="setcard__nombre">{s.name}</span>
                  <span className="setcard__datos muted">
                    {s.releaseDate && (
                      <>
                        {new Date(`${String(s.releaseDate).slice(0, 10)}T12:00:00`).toLocaleDateString(
                          lang === 'es' ? 'es-CL' : 'en-US',
                          { month: 'short', year: 'numeric' }
                        )}
                        {' · '}
                      </>
                    )}
                    {t('setNCards', { n: s.count })}
                  </span>
                  {/* Cuanto del set se ve en torneos (90 dias): responde de un
                      vistazo si es un set competitivo o de coleccion. */}
                  {s.playedPct != null && s.playedPct > 0 && (
                    <span className="setcard__uso">{t('setPlayedPct', { n: s.playedPct })}</span>
                  )}
                  {s.ptcglCode && <span className="setcard__code">{s.ptcglCode}</span>}
                </button>
              </li>
            ))}
          </ul>
        )
      ) : (
      <>
      <div className="searchbar">
        <input
          className="searchbar__input"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('searchPlaceholder')}
          aria-label={t('search')}
        />
        {/* Solo existe en telefono (lo esconde el CSS): abre y cierra los
            filtros. El numerito dice cuantos hay puestos sin tener que abrir. */}
        <button
          type="button"
          className="btn btn--ghost filtrosbtn"
          aria-expanded={verFiltros}
          aria-controls="filtros"
          onClick={() => setVerFiltros((v) => !v)}
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="filtrosbtn__icono">
            <path
              d="M4 6h16M7 12h10m-7 6h4"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
            />
          </svg>
          {t('filters')}
          {(() => {
            const n = Object.entries(filters).filter(([k, v]) => v !== EMPTY_FILTERS[k]).length;
            return n > 0 ? <span className="filtrosbtn__n">{n}</span> : null;
          })()}
        </button>
      </div>

      <div className={`filters${verFiltros ? ' is-open' : ''}`} id="filtros">
        <Dropdown
          label={t('format')}
          value={filters.legal}
          porDefecto={EMPTY_FILTERS.legal}
          placeholder={t('any')}
          onChange={setFilter('legal')}
          options={[
            anyOption,
            { key: 'standard', label: t('standard') },
            { key: 'expanded', label: t('expanded') },
          ]}
        />
        <Dropdown label={t('type')} value={filters.type} placeholder={t('any')}
          onChange={setFilter('type')} options={opts(meta?.types)} />
        <Dropdown label={t('category')} value={filters.category} placeholder={t('any')}
          onChange={setFilter('category')} options={opts(meta?.categories)} />
        <Dropdown label={t('stage')} value={filters.stage} placeholder={t('any')}
          onChange={setFilter('stage')} options={opts(meta?.stages)} />
        <Dropdown label={t('rarity')} value={filters.rarity} placeholder={t('any')}
          onChange={setFilter('rarity')} options={opts(meta?.rarities)} />
        <Dropdown label={t('set')} value={filters.set} placeholder={t('any')}
          onChange={setFilter('set')}
          options={opts(meta?.sets?.map((s) => ({ key: s.id, label: s.name })))} />
        {/* La puerta de los avanzados. Mismo rótulo siempre («Más filtros»,
            con chevron que gira y el numerito de cuántos hay puestos):
            alternar a «Menos filtros» cambiaba el ancho del botón y la
            rejilla daba un salto — la lección de las flechas del paginador. */}
        <button
          type="button"
          className={`filters__mastoggle${masFiltros ? ' is-open' : ''}`}
          aria-expanded={masFiltros}
          aria-controls="filtros-avanzados"
          onClick={() => setMasFiltros((v) => !v)}
        >
          {t('moreFilters')}
          {(() => {
            const n = FILTROS_AVANZADOS.filter((k) => filters[k] !== EMPTY_FILTERS[k]).length;
            return n > 0 ? <span className="filtrosbtn__n">{n}</span> : null;
          })()}
          <svg className="filters__masflecha" viewBox="0 0 12 8" aria-hidden="true">
            <path d="M1 1.5 6 6.5 11 1.5" fill="none" stroke="currentColor" strokeWidth="1.7" />
          </svg>
        </button>
        {/* `display: contents` en CSS: los avanzados entran a la MISMA rejilla
            que los de siempre, sin caja intermedia que los desalinee. Nada de
            overflow ni animación de altura aquí — el desplegable abierto de un
            filtro se recortaría (la mina conocida de la casa). */}
        <div className="filters__mas" id="filtros-avanzados" hidden={!masFiltros}>
        {/* Avanzados, estilo Limitless: tipo de Entrenador, marca de
            reglamento y tramo de PS. Los tramos son neutrales al idioma. */}
        <Dropdown label={t('filterTrainerType')} value={filters.trainerType} placeholder={t('any')}
          onChange={setFilter('trainerType')} options={opts(meta?.trainerTypes)} />
        <Dropdown label={t('filterMark')} value={filters.mark} placeholder={t('any')}
          onChange={setFilter('mark')}
          options={opts(meta?.regulationMarks?.map((m) => ({ key: m, label: m })))} />
        <Dropdown label={t('hp')} value={filters.hp} placeholder={t('any')}
          onChange={setFilter('hp')}
          options={opts([
            { key: '-70', label: '≤ 70' },
            { key: '80-140', label: '80 – 140' },
            { key: '150-230', label: '150 – 230' },
            { key: '240-', label: '240 +' },
          ])} />
        <Dropdown label={t('retreat')} value={filters.retreat} placeholder={t('any')}
          onChange={setFilter('retreat')}
          options={opts([
            { key: '0-0', label: '0' },
            { key: '1-1', label: '1' },
            { key: '2-2', label: '2' },
            { key: '3-', label: '3 +' },
          ])} />
        <Dropdown label={t('weakness')} value={filters.weakness} placeholder={t('any')}
          onChange={setFilter('weakness')} options={opts(meta?.types)} />
        <Dropdown label={t('filterAbility')} value={filters.ability} placeholder={t('any')}
          onChange={setFilter('ability')}
          options={opts([{ key: '1', label: t('abilityYes') }])} />
        {/* Texto libre, como en Limitless: nadie recuerda "5ban Graphics"
            completo. Escribe con calma: el valor se aplica con retardo para
            no disparar una consulta por tecla. */}
        <label className={`filters__texto${filters.artist ? ' is-filtrado' : ''}`}>
          <span className="dropdown__label">{t('filterArtist')}</span>
          <input
            className="filters__input"
            type="search"
            value={artistLocal}
            onChange={(e) => setArtistLocal(e.target.value)}
            placeholder={t('any')}
          />
        </label>
        </div>
      </div>

      {/*
        La fila de chips vive FUERA del panel plegable: en movil es lo unico
        que dice que hay filtros puestos sin abrir nada. "Limpiar filtros" se
        mudo aqui con ellos -- limpia lo que los chips enseñan, y juntos se
        leen como una sola herramienta.
      */}
      {filtersActive && (
        <div className="fchips">
          {query.trim() !== '' && (
            <button
              type="button"
              className="fchip"
              onClick={() => setQuery('')}
              title={t('search')}
            >
              <span className="fchip__k">{t('search')}</span> «{query.trim()}»
              <span className="fchip__x" aria-hidden="true">✕</span>
            </button>
          )}
          {filtrosPuestos.map(([k, v]) => (
            <button
              key={k}
              type="button"
              className="fchip"
              onClick={() => setFilter(k)(EMPTY_FILTERS[k])}
              title={t(NOMBRE_FILTRO[k])}
            >
              <span className="fchip__k">{t(NOMBRE_FILTRO[k])}</span> {etiquetaFiltro(k, v)}
              <span className="fchip__x" aria-hidden="true">✕</span>
            </button>
          ))}
          <button
            type="button"
            className="filters__clear"
            onClick={() => {
              setFilters(EMPTY_FILTERS);
              setQuery('');
            }}
          >
            {t('clearFilters')}
          </button>
        </div>
      )}

      <p className="resultcount muted">
        {cifra(total, lang)} {t('results')}
      </p>

      {cards.length === 0 && !busy ? (
        <Vacio tipo="pokebola" titulo={t('noResults')} texto={t('noResultsHint')} />
      ) : (
        <ul className="grid">
          {cards.map((c) => (
            <li key={c.id}>
              {/*
                Enlace y no boton: /carta/ID es la URL indexable de la ficha y
                el href de la rejilla es como el robot la descubre. Para una
                persona sigue siendo el modal de siempre (se intercepta el
                clic); ctrl+clic abre la carta en otra pestaña, de regalo.
              */}
              <a
                className="card"
                href={`/carta/${encodeURIComponent(c.id)}`}
                onClick={(e) => {
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                  e.preventDefault();
                  setOpenCard(c.id);
                }}
              >
                {cardArt(c) ? (
                  /*
                   * DOS RESOLUCIONES, que las elija el navegador.
                   *
                   * La rejilla pedia siempre `low` (245 px de ancho) y la carta
                   * se pinta a 168 px: en una pantalla normal sobra, pero en
                   * cualquier movil o portatil retina (dpr 2) hacen falta 335 y
                   * llegaban 245 -- el arte se veia BLANDO en la mayoria de las
                   * pantallas de hoy, que es justo donde mas se mira.
                   *
                   * Descriptores de densidad (1x/2x) y no de ancho (w): el
                   * problema aqui es la densidad de la pantalla, no el hueco de
                   * la rejilla, y asi no hay que mantener un `sizes` con los
                   * breakpoints copiados que se descuadre al tocar el CSS.
                   */
                  <Arte
                    className="card__img"
                    src={cardArt(c)}
                    srcSet={`${cardArt(c)} 1x, ${cardArt(c, 'high')} 2x`}
                    alt={c.name}
                    loading="lazy"
                  />
                ) : (
                  /* Un rectangulo vacio se lee como un fallo de carga. Decirlo
                     deja claro que la carta esta y lo que falta es la
                     ilustracion: TCGdex no ilustra promos ni energias basicas,
                     y no todas se recuperan de la fuente de respaldo. */
                  <div className="card__img card__img--none">
                    <span>{t('noArt')}</span>
                  </div>
                )}
                <span className="card__name">{c.name}</span>
                <span className="card__meta muted">
                  {c.setName} · {c.localId}
                </span>
                {c.isFallback && <span className="card__flag" title={t('fallbackNotice')}>EN</span>}
              </a>
            </li>
          ))}
        </ul>
      )}

      {busy && <Cargando texto={t('loading')} />}

      <Paginador
        pagina={pagina}
        total={Math.max(1, Math.ceil(total / pageSize))}
        onPagina={irPagina}
      />

      {openCard && (() => {
        /*
         * Vecinos dentro de la rejilla cargada, para navegar sin cerrar. Si la
         * carta se abrio desde otro sitio y no esta en la lista (idx -1), no se
         * ofrece navegacion: mejor sin flechas que con flechas que saltan a un
         * sitio sin relacion.
         */
        const idx = cards.findIndex((c) => c.id === openCard);
        const prev = idx > 0 ? cards[idx - 1].id : null;
        const next = idx >= 0 && idx < cards.length - 1 ? cards[idx + 1].id : null;
        return (
          <CardDetail
            cardId={openCard}
            onClose={() => setOpenCard(null)}
            onPrev={prev ? () => setOpenCard(prev) : undefined}
            onNext={next ? () => setOpenCard(next) : undefined}
          />
        );
      })()}
      </>
      )}
      </>
      )}
      </main>

      <footer className="foot">
        <div className="foot__left">
          <Logo size={20} />
          <p className="foot__note">
            {t('footNote')}{' '}
            <button type="button" className="foot__funciones" onClick={() => setVerFunciones(true)}>
              {t('seeAllFeatures')}
            </button>
            {/* Que ESCUCHAMOS va dicho donde se lee el resto de la letra del
                proyecto, con el buzon a un clic. */}
            <span className="foot__sep" aria-hidden="true"> · </span>
            {t('fbFootLine')}{' '}
            <button type="button" className="foot__funciones" onClick={() => setVerSugerencias(true)}>
              {t('fbFootCta')}
            </button>
            {/* La política, siempre a un clic desde cualquier pantalla: es lo
                que la ley entiende por "publicada de forma permanente". */}
            <span className="foot__sep" aria-hidden="true"> · </span>
            <button type="button" className="foot__funciones" onClick={() => setVerPrivacidad(true)}>
              {t('privacyLink')}
            </button>
            {/* De donde salen los numeros, a un clic desde cualquier pantalla:
                es la pagina que sostiene todo lo que promete el sitio. */}
            <span className="foot__sep" aria-hidden="true"> · </span>
            <button type="button" className="foot__funciones" onClick={() => setVerMetodologia(true)}>
              {t('methodologyLink')}
            </button>
            {/* La app de escritorio se sirve desde /descargas (server/data,
                fuera de git): el enlace es un <a> de verdad, no un modal. */}
            <span className="foot__sep" aria-hidden="true"> · </span>
            <a className="foot__funciones" href="/descargas/PokeTrack-Setup.exe" title={t('desktopAppNote')}>
              {t('desktopAppLink')}
            </a>
          </p>
        </div>
        {/* El idioma vive aqui (y en el panel de cuenta): se elige una vez y
            se autodetecta, no merece sitio en la fila de herramientas. */}
        <div className="foot__lang">
          <LangSwitch />
        </div>
        {/*
          LA FIRMA: credito y version en una sola columna alineada a la
          derecha, para que la fila del pie tenga tres bloques parejos
          (aviso · idioma · firma) y no cuatro items flotando a alturas
          distintas. La version (package.json raiz + commit + fecha de build,
          inyectados por Vite) contesta "¿que estas viendo?"; el © con el año
          es el aviso legal de siempre, con su simbolo de verdad.
        */}
        <div className="foot__firma">
          <a
            className="credito-asery"
            href="https://asery.cl?utm_source=poketrack"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('madeBy')} <strong>A-Sery lab</strong>
          </a>
          <p className="foot__version muted">
            v{__APP_VERSION__}
            {__APP_COMMIT__ ? ` · ${__APP_COMMIT__}` : ''} · {__APP_BUILT__}
            {' · '}© {new Date().getFullYear()} A-Sery lab
          </p>
        </div>
      </footer>

      <Suspense fallback={null}>
        <Funciones abierto={verFunciones} onClose={() => setVerFunciones(false)} />
        <Sugerencias abierto={verSugerencias} onClose={() => setVerSugerencias(false)} />
        <Privacidad abierto={verPrivacidad} onClose={() => setVerPrivacidad(false)} />
        <Metodologia abierto={verMetodologia} onClose={() => setVerMetodologia(false)} />
        <Buscador
          abierto={verBuscador}
          onClose={() => setVerBuscador(false)}
          /*
           * Cada resultado lleva a DONDE VIVE, no a una pagina de resultados:
           * una carta abre su ficha encima de lo que estabas mirando, un
           * arquetipo despliega su fila en el meta y un mazo se abre en el
           * constructor. Buscar no deberia costar perder el sitio.
           */
          onElegir={(tipo, dato) => {
            if (tipo === 'carta') return abrirCarta(dato.id);
            if (tipo === 'arquetipo') {
              setArquetipoInicial({ id: dato.id, nombre: dato.name, n: (arquetipoInicial?.n ?? 0) + 1 });
              return setView('meta');
            }
            setMazoInicial({ id: dato.id, n: (mazoInicial?.n ?? 0) + 1 });
            return setView('decks');
          }}
        />
      </Suspense>
    </div>
  );
}
